import { useMemo, useState } from 'react';

import ls from 'local-storage';

import { TAG_FILTER_MODE_DEFAULT, sliceData } from '@libs/NwUtils';

import { useContactPlaylists } from '@hooks/API/Contact/UseContactPlaylists';

import {
    BOOKS_CHARACTERISTIC_FILTERS,
    BOOKS_SORT_STORAGE_KEY,
    addMineSortToPlaylists,
    coercePlaylistArray,
    filterPlaylistsByCharacteristic,
    filterPackagePlaylistsWithMedia,
    filterPlaylistsByOwner,
    filterPlaylistsByTags,
    sortPlaylistsForBooks,
} from './modelDetailsBooks.utils';

const DEFAULT_BOOKS_SORT = 'published';

export function useModelDetailsBooksList({
    contactID,
    modelPlaylists,
    scope,
    currentUserId,
    packageSectionActive = false,
    itemsPerPage = 4,
    shouldPaginate = false,
    websiteData = [],
    isMobile = false,
    // Su mobile lo switch published/mine è l'unico controllo di ordinamento disponibile
    allowManualSort = isMobile || scope === 'package' || scope === 'books-drawer',
    enableDrawerFilters = scope === 'books-drawer',
    enableCharacteristicFilter = scope === 'model-details' ||
        scope === 'books-drawer' ||
        scope === 'model-drawer' ||
        scope === 'package',
}) {
    const savedBooksSort = allowManualSort ? ls.get(BOOKS_SORT_STORAGE_KEY) : null;
    const [booksSort, setBooksSortState] = useState(savedBooksSort || DEFAULT_BOOKS_SORT);
    const [activePage, setActivePage] = useState(1);
    const [filterTags, setFilterTags] = useState([]);
    const [tagFilterMode, setTagFilterMode] = useState(TAG_FILTER_MODE_DEFAULT);
    const [onlyMine, setOnlyMine] = useState(false);
    const [playlistCharacteristicFilter, setPlaylistCharacteristicFilter] = useState(BOOKS_CHARACTERISTIC_FILTERS.ALL);

    const disableQuery = Boolean(modelPlaylists);
    const { data: fetchedPlaylists, isFetching: isFetchingPlaylists } = useContactPlaylists(contactID, disableQuery);

    const playlists = useMemo(() => {
        const fromQuery = coercePlaylistArray(fetchedPlaylists);
        const fromProp = coercePlaylistArray(modelPlaylists);

        if (!isFetchingPlaylists && Array.isArray(fromQuery)) {
            return [...fromQuery];
        }
        if (fromProp) {
            return [...fromProp];
        }

        return null;
    }, [fetchedPlaylists, isFetchingPlaylists, modelPlaylists]);

    const hasAnyFastPackage = useMemo(() => Boolean(playlists?.some((book) => book.FastPackage)), [playlists]);

    const displayedBooks = useMemo(() => {
        if (!playlists) {
            return [];
        }

        const filteredByScope = enableDrawerFilters
            ? filterPlaylistsByOwner(
                  filterPlaylistsByTags(playlists, filterTags, tagFilterMode),
                  currentUserId,
                  onlyMine
              )
            : [...playlists];
        const characteristicFilteredBooks = enableCharacteristicFilter
            ? filterPlaylistsByCharacteristic(filteredByScope, playlistCharacteristicFilter, websiteData)
            : filteredByScope;

        const booksWithMineSort = addMineSortToPlaylists(characteristicFilteredBooks, currentUserId);
        const packageFilteredBooks = packageSectionActive
            ? filterPackagePlaylistsWithMedia(booksWithMineSort)
            : booksWithMineSort;

        return sortPlaylistsForBooks(packageFilteredBooks, booksSort, allowManualSort);
    }, [
        allowManualSort,
        booksSort,
        currentUserId,
        enableCharacteristicFilter,
        enableDrawerFilters,
        filterTags,
        tagFilterMode,
        onlyMine,
        packageSectionActive,
        playlistCharacteristicFilter,
        playlists,
        websiteData,
    ]);

    const booksToRender = useMemo(() => {
        if (!shouldPaginate) {
            return displayedBooks;
        }

        return sliceData(activePage, itemsPerPage, displayedBooks);
    }, [activePage, displayedBooks, itemsPerPage, shouldPaginate]);

    const setBooksSort = (value) => {
        setBooksSortState(value);
        if (allowManualSort) {
            ls.set(BOOKS_SORT_STORAGE_KEY, value);
        }
    };

    return {
        activePage,
        booksSort,
        booksToRender,
        displayedBooks,
        filterTags,
        tagFilterMode,
        hasAnyFastPackage,
        onlyMine,
        playlistCharacteristicFilter,
        playlists,
        setActivePage,
        setBooksSort,
        setFilterTags,
        setTagFilterMode,
        setOnlyMine,
        setPlaylistCharacteristicFilter,
    };
}
